import { create } from 'zustand';
import { Folder, FolderCreateRequest, FolderUpdateRequest } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';

interface FolderState {
  folders: Folder[];
  isLoading: boolean;
  error: string | null;
}

interface FolderStore extends FolderState {
  fetchFolders: () => Promise<void>;
  createFolder: (folderData: FolderCreateRequest) => Promise<Folder>;
  updateFolder: (folderId: string, updateData: FolderUpdateRequest) => Promise<void>;
  deleteFolder: (folderId: string, force?: boolean) => Promise<void>;
  clearError: () => void;
}

export const useFolderStore = create<FolderStore>((set, get) => ({
  folders: [],
  isLoading: false,
  error: null,

  fetchFolders: async () => {
    set({ isLoading: true, error: null });
    try {
      // Only fetch user folders (folder_type='user' includes data source containers), not global
      const folders = await apiService.getFolders(false, 'user');
      set({ folders, isLoading: false });
    } catch (error: any) {
      // If 401, don't show error toast (auth interceptor will handle redirect)
      if (error.response?.status === 401) {
        set({ folders: [], isLoading: false, error: null });
        return;
      }
      const message = error.response?.data?.message || 'Failed to fetch folders';
      set({ error: message, isLoading: false });
      toast.error(message);
    }
  },

  createFolder: async (folderData: FolderCreateRequest) => {
    set({ isLoading: true, error: null });
    try {
      const newFolder = await apiService.createFolder(folderData);
      
      // Refresh folders to get updated structure
      await get().fetchFolders();
      
      // No toast - folder appears in UI automatically
      return newFolder;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create folder';
      set({ error: message, isLoading: false });
      toast.error(message);
      throw error;
    }
  },

  updateFolder: async (folderId: string, updateData: FolderUpdateRequest) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.updateFolder(folderId, updateData);
      // Refresh folders to get updated structure (don't fail the whole update if refresh fails)
      try {
        await get().fetchFolders();
      } catch (refreshError: any) {
        console.warn('Folder updated but refresh failed:', refreshError);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update folder';
      set({ error: message, isLoading: false });
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteFolder: async (folderId: string, force: boolean = false) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.deleteFolder(folderId, force);
      
      // Refresh folders to get updated structure
      await get().fetchFolders();
      
      toast.success('Folder deleted successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete folder';
      set({ error: message, isLoading: false });
      toast.error(message);
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
